(() => {
  const feeTiers = [
    { maximum: 20, variantId: 50546057576677, perBox: false },
    { maximum: 50, variantId: 50546057609445, perBox: false },
    { maximum: 100, variantId: 50546057642213, perBox: false },
    { maximum: 200, variantId: 50546057674981, perBox: false },
    { maximum: 500, variantId: 50546057707749, perBox: true },
    { maximum: Infinity, variantId: 50546057740517, perBox: true },
  ];

  const foldingBoxHandles = new Set([
    'small-magnetic-folding-gift-box',
    'medium-magnetic-folding-gift-box',
    'large-standard-magnetic-folding-gift-box',
    'small-ribbon-magnetic-folding-gift-box',
    'medium-ribbon-magnetic-folding-gift-box',
    'large-ribbon-magnetic-folding-gift-box',
    'small-magnetic-folding-gift-box-with-handle',
    'medium-magnetic-folding-gift-box-with-handle',
    'large-magnetic-folding-gift-box-with-handle',
    'small-3-color-magnetic-folding-gift-box',
    'medium-3-color-magnetic-folding-gift-box',
    'large-3-color-magnetic-folding-gift-box',
    'custom-logo-folding-box',
    'colorful-paper-folding-box-custom-printed-gift-box-in-stock',
    'tall-magnetic-folding-gift-box',
    'extra-large-magnetic-folding-gift-box',
    'ready-stock-drawer-jewelry-boxes',
  ]);

  const root = () => window.Shopify?.routes?.root || '/';

  const fetchCart = async () => {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    const cart = await response.json();
    if (!response.ok) throw new Error('The cart could not be checked.');
    return cart;
  };

  const validateCart = (cart) => {
    const groups = new Map();
    const issues = [];

    (cart?.items || []).forEach((item) => {
      const role = item.properties?._slate_logo_role;

      if (role !== 'fee' && foldingBoxHandles.has(item.handle) && Number(item.quantity) < 20) {
        issues.push({ type: 'below-minimum', item, minimum: 20 });
      }

      if (role !== 'base' && role !== 'fee') return;

      const group = item.properties?._slate_logo_group;
      if (!group) {
        issues.push({ type: 'missing-group', item });
        return;
      }

      if (!groups.has(group)) groups.set(group, { bases: [], fees: [] });
      groups.get(group)[role === 'base' ? 'bases' : 'fees'].push(item);
    });

    groups.forEach(({ bases, fees }, group) => {
      if (bases.length !== 1 || fees.length !== 1) {
        issues.push({ type: 'missing-or-duplicate-line', group });
        return;
      }

      const base = bases[0];
      const fee = fees[0];
      const boxQuantity = Number(base.quantity);
      if (base.handle === 'ready-stock-drawer-jewelry-boxes' && Number(base.properties?._slate_logo_location_count) !== 1) {
        issues.push({ type: 'invalid-drawer-location-count', group });
        return;
      }
      const locationCount = Number(base.properties?._slate_logo_location_count);
      const feeLocationCount = Number(fee.properties?._slate_logo_location_count);
      const tier = feeTiers.find((candidate) => boxQuantity <= candidate.maximum);
      const expectedFeeQuantity = tier?.perBox ? boxQuantity * locationCount : locationCount;

      if (
        !Number.isInteger(boxQuantity)
        || boxQuantity < 20
        || ![1, 2].includes(locationCount)
        || feeLocationCount !== locationCount
        || Number(fee.variant_id) !== tier?.variantId
        || Number(fee.quantity) !== expectedFeeQuantity
      ) {
        issues.push({ type: 'incorrect-fee', group });
      }
    });

    return { valid: issues.length === 0, issues };
  };

  const applyCheckoutGuard = (cart) => {
    const result = validateCart(cart);

    document.querySelectorAll('[data-slate-logo-integrity-warning]').forEach((warning) => {
      warning.hidden = result.valid;
    });

    document.querySelectorAll('[data-slate-logo-cart-form] button[name="checkout"]').forEach((button) => {
      button.disabled = !result.valid;
      button.toggleAttribute('aria-disabled', !result.valid);
    });

    document.querySelectorAll('[data-slate-logo-accelerated]').forEach((container) => {
      container.hidden = !result.valid;
    });

    return result;
  };

  const refreshCheckoutGuard = async (providedCart) => {
    try {
      const cart = providedCart?.items ? providedCart : await fetchCart();
      return applyCheckoutGuard(cart);
    } catch {
      document.querySelectorAll('[data-slate-logo-cart-form] button[name="checkout"]').forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      });
      document.querySelectorAll('[data-slate-logo-integrity-warning]').forEach((warning) => {
        warning.hidden = false;
      });
      return { valid: false, issues: [{ type: 'cart-check-failed' }] };
    }
  };

  window.SlateLogoCartIntegrity = { validateCart, applyCheckoutGuard, refreshCheckoutGuard };

  document.addEventListener('DOMContentLoaded', () => refreshCheckoutGuard());
  document.documentElement.addEventListener('cart:change', (event) => refreshCheckoutGuard(event.detail?.cart));
  document.documentElement.addEventListener('cart:refresh', () => window.setTimeout(() => refreshCheckoutGuard(), 0));

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target.closest('[data-slate-logo-cart-form]');
      if (!form || event.submitter?.name !== 'checkout') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (form.dataset.slateLogoCheckoutBusy === 'true') return;

      form.dataset.slateLogoCheckoutBusy = 'true';
      event.submitter?.setAttribute('aria-busy', 'true');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      try {
        const cart = await fetchCart();
        const result = applyCheckoutGuard(cart);

        if (!result.valid) {
          const warning = form.querySelector('[data-slate-logo-integrity-warning]');
          warning?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return;
        }

        window.location.assign(`${root()}checkout`);
      } catch {
        window.alert('We could not verify the logo printing charge. Please wait a moment and try checkout again.');
      } finally {
        delete form.dataset.slateLogoCheckoutBusy;
        event.submitter?.removeAttribute('aria-busy');
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    },
    true
  );

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-slate-remove-logo-group]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const group = button.dataset.slateRemoveLogoGroup;
    if (!group || button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const cart = await fetchCart();
      const updates = {};
      cart.items.forEach((item) => {
        if (item.properties?._slate_logo_group === group) updates[item.key] = 0;
      });
      if (!Object.keys(updates).length) throw new Error('The customized set is no longer in the cart.');

      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (!response.ok) throw new Error('The customized set could not be removed.');

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      button.removeAttribute('aria-busy');
      window.alert(error.message || 'The customized set could not be removed. Please try again.');
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  });
})();
